import { NavLink } from 'react-router-dom';
import { ArrowLeftRight, Boxes, FileText, Loader2, ReceiptText, Users, WalletCards } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * The Accounting frame: one heading, and a navigation band split into two
 * labelled groups.
 *
 * Sales and Payments are deliberately *not* one merged list. They are two
 * separately captioned groups with a rule between them, because money coming
 * in and money going out are different jobs done by different people, and the
 * nav is the cheapest place to say so.
 *
 * Products & Services appears in both groups on purpose: it is one catalog
 * filtered by context, not two catalogs.
 *
 * Transactions sits outside both groups, on its own row above them, because it
 * is neither: it is every movement of money, whichever direction it went. A
 * deposit and a withdrawal belong on the same page, so putting it inside
 * either group would have been a lie about what it lists.
 */

const LEDGER_LINKS = [
  { label: 'Transactions', path: '/admin/accounting/transactions', icon: ArrowLeftRight },
];

const SALES_LINKS = [
  { label: 'Invoices', path: '/admin/accounting/sales/invoices', icon: FileText },
  { label: 'Customers', path: '/admin/accounting/sales/customers', icon: Users },
  { label: 'Products & Services', path: '/admin/accounting/sales/items', icon: Boxes },
];

const PAYMENT_LINKS = [
  { label: 'Bills', path: '/admin/accounting/payments/bills', icon: ReceiptText },
  { label: 'Products & Services', path: '/admin/accounting/payments/items', icon: Boxes },
];

function NavGroup({ title, links }) {
  return (
    <div className="min-w-0 flex-1 p-3">
      <p className="px-2 pb-2 text-[10px] font-semibold uppercase tracking-[0.14em] text-[#062B1A]/40">
        {title}
      </p>
      <div className="flex flex-wrap gap-1">
        {links.map((link) => (
          <NavLink
            key={link.path}
            to={link.path}
            className={({ isActive }) =>
              cn(
                'inline-flex items-center rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                isActive
                  ? 'bg-[#062B1A] text-white'
                  : 'text-[#062B1A]/60 hover:bg-[#062B1A]/5 hover:text-[#062B1A]'
              )
            }
          >
            <link.icon className="mr-2 size-4" />
            {link.label}
          </NavLink>
        ))}
      </div>
    </div>
  );
}

export function AccountingHeader({ title, description, actions }) {
  return (
    <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
      <div className="min-w-0">
        <p className="text-xs font-semibold uppercase tracking-[0.12em] text-[#D4AF37]">
          Finance · Accounting
        </p>
        <h1
          className="mt-1 text-2xl font-semibold text-[#062B1A] sm:text-3xl"
          style={{ fontFamily: 'var(--font-display)' }}
        >
          {title}
        </h1>
        {description && (
          <p className="mt-2 max-w-3xl text-sm leading-relaxed text-[#062B1A]/60">{description}</p>
        )}
      </div>
      {actions && <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div>}
    </div>
  );
}

export default function AccountingShell({ title, description, actions, children }) {
  return (
    <div className="space-y-5">
      <AccountingHeader title={title} description={description} actions={actions} />
      <nav
        aria-label="Accounting"
        className="overflow-x-auto rounded-xl border border-[#062B1A]/8 bg-white shadow-sm"
      >
        <div className="divide-y divide-[#062B1A]/8">
          <NavGroup title="Ledger" links={LEDGER_LINKS} />
          <div className="flex min-w-max flex-col divide-y divide-[#062B1A]/8 sm:min-w-0 sm:flex-row sm:divide-x sm:divide-y-0">
            <NavGroup title="Sales" links={SALES_LINKS} />
            <NavGroup title="Payments" links={PAYMENT_LINKS} />
          </div>
        </div>
      </nav>
      {children}
    </div>
  );
}

export function AccountingLoading({ label = 'Loading…' }) {
  return (
    <div className="flex min-h-[240px] items-center justify-center rounded-xl border border-[rgba(6,43,26,0.08)] bg-white">
      <div className="text-center text-sm text-[#062B1A]/55">
        <Loader2 className="mx-auto mb-3 size-6 animate-spin text-[#D4AF37]" />
        {label}
      </div>
    </div>
  );
}

export function AccountingEmpty({ title, description, action }) {
  return (
    <div className="rounded-xl border border-dashed border-[#062B1A]/15 bg-white px-6 py-14 text-center">
      <WalletCards className="mx-auto size-10 text-[#062B1A]/20" />
      <h2 className="mt-3 text-base font-semibold text-[#062B1A]">{title}</h2>
      {description && <p className="mx-auto mt-1 max-w-lg text-sm text-[#062B1A]/50">{description}</p>}
      {action && <div className="mt-5">{action}</div>}
    </div>
  );
}

export function AccountingError({ message, onRetry }) {
  return (
    <div className="rounded-xl border border-red-200 bg-red-50 px-5 py-4 text-sm text-red-700">
      <p>{message}</p>
      {onRetry && (
        <button
          type="button"
          onClick={onRetry}
          className="mt-3 rounded-md border border-red-300 bg-white px-3 py-1.5 text-xs font-semibold text-red-700 hover:bg-red-100"
        >
          Try again
        </button>
      )}
    </div>
  );
}
